/**
 * Which services the Shared Access panel shows (app#465).
 *
 * Pure, so `sharedAccess.test.ts` can check it with `node --experimental-strip-types`.
 */

type PanelService = { id: string; name: string; nodeId?: string | null; authRequired?: boolean }
type PanelGrant = { serviceId: string; grantedAt: string | null; rejectedAt: string | null }

/** The ids of the services that have a request nobody answered, sorted, as one stable string. */
export function pendingServiceKey(grants: PanelGrant[]): string {
  const ids = new Set(grants.filter(grant => !grant.grantedAt && !grant.rejectedAt).map(grant => grant.serviceId))
  return [...ids].sort().join(',')
}

/** The union of two id keys, as `pendingServiceKey` writes them. */
export function unionKeys(a: string, b: string): string {
  return [...new Set([...a.split(','), ...b.split(',')].filter(Boolean))].sort().join(',')
}

/**
 * The services of the panel, for its three routes.
 *
 * ⚠⚠ THE PANEL FOR ONE SERVICE ALSO SHOWS EVERY OTHER SERVICE OF THE SAME NODE THAT HAS A PENDING
 * REQUEST (app#465). The access-request mail opens the panel for the service it names. A person
 * who asked for two services of one node sent two mails, and the owner who opened one mail could
 * not see that the other request still waited (support incident, 2026-10-07). The asked service
 * stays first.
 * ⚠ Only services with a PENDING request are added, not every service of the node: the mail is
 * about one service, and the other lists are not its subject.
 * ⚠ The caller gives every id that was pending SINCE THE PANEL OPENED (`unionKeys`), not only the
 * ids pending now. An answer takes the request out of "pending" at once, and the service would
 * leave the panel with its "Accept after all" button, the only way back from a wrong Reject.
 */
export function panelServices<S extends PanelService>(
  services: S[],
  pendingKey: string,
  serviceName?: string,
  nodeId?: string
): S[] {
  if (serviceName) {
    const match = services.find(service => service.name.toLowerCase() === serviceName.toLowerCase())
    if (!match) return []
    const pending = new Set(pendingKey ? pendingKey.split(',') : [])
    // ⚠ `authRequired`: a request made before a service became public needs no answer.
    const others = services.filter(
      service =>
        service.id !== match.id &&
        !!match.nodeId &&
        service.nodeId === match.nodeId &&
        !!service.authRequired &&
        pending.has(service.id)
    )
    return [match, ...others]
  }
  const list = services.filter(service => service.authRequired)
  return nodeId ? list.filter(service => service.nodeId === nodeId) : list
}
